import React, { useContext, useEffect, useState } from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import useAuth from './auth';
import { Api } from '../../types/api';
import { mergeStyleSets, Stack } from '@fluentui/react';
import { useIntl } from '@ysoft/react-intl';
import { jwtDecode } from './jwtDecode';
import { isAuthMocked } from '../config/config';
import { useConfigContext } from '../config/configContext';
import AuthErrorPage from '../components/AuthErrorPage';

const classNames = mergeStyleSets({
	page_loading_spinner: {
		'margin-top': '10rem',
	},
});

const userContext = React.createContext<Api.UserInfo>({isSupportUser: false, tenant_id: ''});

export const useUserContext = (): Api.UserInfo => useContext(userContext);

interface TokenClaims {
	preferred_username?: string;
	given_name?: string;
	family_name?: string;
	name?: string;
	email?: string;
	roles?: string[];
	tid?: string;
	oid?: string;
}

const UserContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { isAuthenticated, account, getAccessToken } = useAuth();
	const { config } = useConfigContext();
	const [user, setUser] = useState<Api.UserInfo>();
	const [tenantDenied, setTenantDenied] = useState(false);
	const { t } = useIntl();

	useEffect(() => {
		const loadUserInfo = async () => {
			if (isAuthenticated && account) {
				try {
					// Get the access token to decode claims
					const accessToken = await getAccessToken();

					let claims: TokenClaims = {};
					if (accessToken) {
						claims = jwtDecode<TokenClaims>(accessToken);
					}

					const tenantId = claims.tid || account.tenantId;

					// Fail-closed; skip in mock mode so integration harnesses aren't blocked.
					if (!isAuthMocked) {
						const allowedTenantIds = config.msal.allowedTenantIds ?? [];
						if (!tenantId || !allowedTenantIds.includes(tenantId)) {
							setTenantDenied(true);
							return;
						}
					}

					// Build user info from account and token claims
					// Use Azure AD 'tid' as tenant_id
					// tenant_name is fetched by TenantsHeaderList component via SWR
					const userInfo: Api.UserInfo = {
						preferred_username: account.username || claims.preferred_username,
						given_name: claims.given_name || account.name?.split(' ')[0],
						family_name: claims.family_name || account.name?.split(' ').slice(1).join(' '),
						email: claims.email || account.username,
						tenant_id: tenantId,
						roles: claims.roles || [],
						isSupportUser: claims.roles?.includes('omni-support') || false
					};

					setUser(userInfo);
				} catch (error) {
					console.error('Error loading user info:', error);
					// Fallback to basic account info with tenant from account
					setUser({
						preferred_username: account.username,
						given_name: account.name?.split(' ')[0],
						family_name: account.name?.split(' ').slice(1).join(' '),
						tenant_id: account.tenantId,
						isSupportUser: false
					});
				}
			}
		};

		loadUserInfo();
	}, [isAuthenticated, account, getAccessToken, config.msal.allowedTenantIds]);

	if (tenantDenied) {
		return <AuthErrorPage variant="tenantNotAllowed" />;
	}

	if (user === undefined) {
		return (
			<Stack
				data-testid="platform-spinner"
				horizontalAlign="center"
				verticalAlign="center"
				className={classNames.page_loading_spinner}
			>
				<Spinner size={SpinnerSize.large} label={t('general__loading')} />
			</Stack>
		);
	}

	const customUser: Api.UserInfo = {
		...user
	};

	return (
		<userContext.Provider value={customUser}>
			{children}
		</userContext.Provider>
	);
};

export default UserContextProvider;
